import { useEffect, useRef, useState } from 'react';
import type { ConnectionStatus, SocketEvent } from '../types';

const INITIAL_RECONNECT_MS = 2000;
const MAX_RECONNECT_MS = 30_000;

/**
 * Maintains a WebSocket connection with automatic exponential-backoff reconnection.
 * Calls `onEvent` for every parsed message received from the server.
 */
export function useWebSocket(
  url: string,
  onEvent: (event: SocketEvent) => void,
): { status: ConnectionStatus } {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent; // always up-to-date without re-running the effect

  useEffect(() => {
    let ws: WebSocket;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let delay = INITIAL_RECONNECT_MS;
    let destroyed = false;

    function connect() {
      if (destroyed) return;

      setStatus('connecting');
      ws = new WebSocket(url);

      ws.onopen = () => {
        setStatus('connected');
        delay = INITIAL_RECONNECT_MS;
      };

      ws.onmessage = (e: MessageEvent<string>) => {
        try {
          const event = JSON.parse(e.data) as SocketEvent;
          onEventRef.current(event);
        } catch {
          // Ignore malformed messages — never crash on server output
        }
      };

      ws.onclose = () => {
        if (!destroyed) {
          setStatus('disconnected');
          reconnectTimer = setTimeout(() => {
            delay = Math.min(delay * 2, MAX_RECONNECT_MS);
            connect();
          }, delay);
        }
      };

      ws.onerror = () => {
        ws.close(); // triggers onclose → schedule reconnect
      };
    }

    connect();

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [url]); // url is a constant in practice — effect runs once

  return { status };
}
